import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputLabel,
  FormHelperText,
  Checkbox,
  Box,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

import { Button } from "components/Button";
import { colors } from "theme/colors";

import { StyledTextField } from "components/Input/styles";

import { RiskMatrixConfigDialogProps } from "./types";
import { RISK_MATRIX_CONSTANTS } from "../constants";

const SIZE_LIMITS_ID = "risk-matrix-size-limits";

export const RiskMatrixConfigDialog: React.FC<RiskMatrixConfigDialogProps> = ({
  open,
  configData,
  onConfigDataChange,
  onSave,
  onCancel,
}) => {
  const handleClose = (): void => {
    onCancel();
  };

  const handleLabelChange = (type: "impact" | "likelihood", index: number, value: string): void => {
    const newLabels =
      type === "impact" ? [...configData.impactLabels] : [...configData.likelihoodLabels];
    newLabels[index] = value;

    onConfigDataChange({
      ...configData,
      [type === "impact" ? "impactLabels" : "likelihoodLabels"]: newLabels,
    });
  };

  const handleSizeChange = (type: "rows" | "columns", value: number): void => {
    const newValue = Math.max(
      RISK_MATRIX_CONSTANTS.MIN_SIZE,
      Math.min(RISK_MATRIX_CONSTANTS.MAX_SIZE, value),
    );

    const newConfig = { ...configData, [type]: newValue };

    // Adjust labels arrays to match new size
    if (type === "rows") {
      const newLikelihoodLabels = [...configData.likelihoodLabels];
      while (newLikelihoodLabels.length < newValue) {
        newLikelihoodLabels.push(`Level ${newLikelihoodLabels.length + 1}`);
      }
      while (newLikelihoodLabels.length > newValue) {
        newLikelihoodLabels.pop();
      }
      newConfig.likelihoodLabels = newLikelihoodLabels;
    } else {
      const newImpactLabels = [...configData.impactLabels];
      while (newImpactLabels.length < newValue) {
        newImpactLabels.push(`Level ${newImpactLabels.length + 1}`);
      }
      while (newImpactLabels.length > newValue) {
        newImpactLabels.pop();
      }
      newConfig.impactLabels = newImpactLabels;
    }

    onConfigDataChange(newConfig);
  };

  const { MIN_SIZE, MAX_SIZE } = RISK_MATRIX_CONSTANTS;

  const handleShowSummaryChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    onConfigDataChange({ ...configData, showSummary: e.target.checked });
  };

  return (
    <Dialog
      fullWidth
      open={open}
      maxWidth="md"
      disableAutoFocus
      disableScrollLock
      keepMounted={false}
      disableRestoreFocus
      onClose={handleClose}
    >
      <DialogTitle>Configure Risk Matrix Size and Labels</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", marginTop: "16px" }}>
          <Box>
            <p style={{ fontSize: "16px", margin: "4px 0" }}>Matrix Size</p>
            <Box
              sx={{
                display: "flex",
                gap: "16px",
                marginBottom: "16px",
                alignItems: "center",
              }}
            >
              <div>
                <InputLabel>Rows</InputLabel>
                <StyledTextField
                  variant="outlined"
                  type="number"
                  value={configData.rows}
                  onChange={(e) => handleSizeChange("rows", parseInt(e.target.value, 10) || 5)}
                  inputProps={{ min: MIN_SIZE, max: MAX_SIZE, "aria-describedby": SIZE_LIMITS_ID }}
                  size="small"
                />
              </div>
              <CloseIcon sx={{ fontSize: "24px", fontWeight: 600, color: colors.grey[600] }} />
              <div>
                <InputLabel>Columns</InputLabel>
                <StyledTextField
                  variant="outlined"
                  type="number"
                  value={configData.columns}
                  onChange={(e) => handleSizeChange("columns", parseInt(e.target.value, 10) || 5)}
                  inputProps={{ min: MIN_SIZE, max: MAX_SIZE, "aria-describedby": SIZE_LIMITS_ID }}
                  size="small"
                />
              </div>
            </Box>
            <FormHelperText id={SIZE_LIMITS_ID}>
              Rows and columns must each be between {MIN_SIZE} and {MAX_SIZE}.
            </FormHelperText>
          </Box>

          <div>
            <Box sx={{ display: "flex", gap: "16px" }}>
              <Box sx={{ flex: 1 }}>
                <p style={{ fontSize: "16px", margin: "4px 0" }}>Impact Labels</p>
                {configData.impactLabels.map((label, index) => (
                  <StyledTextField
                    key={index}
                    variant="outlined"
                    value={label}
                    onChange={(e) => handleLabelChange("impact", index, e.target.value)}
                    size="small"
                    fullWidth
                    style={{ marginBottom: "8px" }}
                  />
                ))}
              </Box>
              <Box sx={{ flex: 1 }}>
                <p style={{ fontSize: "16px", margin: "4px 0" }}>Likelihood Labels</p>
                {configData.likelihoodLabels.map((label, index) => (
                  <StyledTextField
                    key={index}
                    variant="outlined"
                    value={label}
                    onChange={(e) => handleLabelChange("likelihood", index, e.target.value)}
                    size="small"
                    fullWidth
                    style={{ marginBottom: "8px" }}
                  />
                ))}
              </Box>
            </Box>
          </div>
        </Box>

        <Box
          role="button"
          tabIndex={0}
          sx={{
            display: "flex",
            marginTop: "24px",
            borderRadius: "4px",
            alignItems: "center",
            backgroundColor: colors.grey[100],
            cursor: "pointer",
          }}
          onClick={() =>
            handleShowSummaryChange({
              target: { checked: !configData.showSummary },
            } as React.ChangeEvent<HTMLInputElement>)
          }
          aria-label="Toggle show summary option"
        >
          <Checkbox checked={configData.showSummary} onChange={handleShowSummaryChange} />
          <p style={{ margin: 0, fontSize: "14px", color: colors.grey[600] }}>
            Show summary in the document after saving the configuration.
          </p>
        </Box>

        <Box
          sx={{
            marginTop: "24px",
            padding: "12px",
            backgroundColor: colors.grey[100],
            borderRadius: "4px",
          }}
        >
          <p style={{ margin: 0, fontSize: "14px", color: colors.grey[600] }}>
            The matrix will be updated in the document after saving the configuration.
          </p>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onSave}>
          Save Configuration
        </Button>
      </DialogActions>
    </Dialog>
  );
};
